import { FC, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { unwrapResult } from '@reduxjs/toolkit';
import { toast } from 'react-toastify';
import { Dayjs } from 'dayjs';

import { CustomButton, CustomCheckBox, CustomSelect, Picker, Text } from '../../shared/uiComponents';
import { PRIMARY, TEXT as TEXT_COLORS } from '../../shared/Helpers/colors';
import { errorNormalizer } from '../../shared/Helpers/functions';
import { InfoFilled } from '../../shared/Assets/svgIcons';

import { DispatchProperties, useSelector } from '../../redux/store';
import { createCancellationRequest, getPendingRequests } from '../../redux/State/clientSlice/scheduleCancellationSlice';
import { getUserClients } from '../../redux/State/clientSlice/userClientSlice';
import {
	CancellationReason,
	CancellingParty,
	extendedClientReasonOptions,
	extendedProviderReasonOptions,
} from '../../redux/API/ClientAPIHelpers/scheduleCancellation';
import CancellationFormFields, { useCancellationForm } from '../../components/CancellationFormFields/useCancellationForm';

interface RequestCancellationModalProps {
	clientId?: string;
	clientName?: string;
	onClose: () => void;
	onSuccess: () => void;
	providerId?: string;
	providerName?: string;
	recurringScheduleId?: string;
}

const clientScopeOptions = [
	{ id: 'all', label: 'All my clients' },
	{ id: 'specific', label: 'Specific clients only' },
];

const isOtherReasonCheck = (reasonId: string) =>
	Number(reasonId) === CancellationReason.ExtendedProviderOther || Number(reasonId) === CancellationReason.ExtendedClientOther;

const cancellingPartyOptions = [
	{
		id: `${CancellingParty.Provider}`,
		label: 'I am requesting time off (BT)',
		description: 'You will be unavailable for your scheduled sessions',
	},
	{
		id: `${CancellingParty.ClientFamily}`,
		label: 'Client/Family is requesting time off',
		description: 'The client or parent initiated the cancellation',
	},
	{
		id: `${CancellingParty.Agency}`,
		label: 'Agency is canceling',
		description: 'The agency initiated the cancellation',
	},
];

const RequestCancellationModal: FC<RequestCancellationModalProps> = ({ onClose, onSuccess, providerId, providerName, clientId, clientName, recurringScheduleId }) => {
	const dispatch = useDispatch<DispatchProperties>();
	const submitting = useSelector((state) => state.scheduleCancellation.submitting);
	const currentUserId = useSelector((state) => state.account.user.id);
	const userId = providerId || currentUserId;
	const clients = useSelector((state) => state.userClient.userClients.query ?? []);

	const [startDate, setStartDate] = useState<Dayjs | null>(null);
	const [endDate, setEndDate] = useState<Dayjs | null>(null);
	const [clientScope, setClientScope] = useState(clientId ? 'specific' : 'all');
	const [selectedClientIds, setSelectedClientIds] = useState<string[]>(clientId ? [clientId] : []);

	const {
		cancellingParty,
		handleCancellingPartyChange,
		isAgency,
		isFormValid,
		isOtherReason,
		notes,
		notesRequired,
		reasonId,
		setNotes,
		setReasonId,
		setSubmitted,
		submitted,
	} = useCancellationForm(isOtherReasonCheck);

	useEffect(() => {
		if (userId && !clientId) {
			dispatch(getUserClients({ page: '1', pageSize: '100', userId }));
		}
	}, [dispatch, userId, clientId]);

	const isSpecific = clientScope === 'specific';

	const isValid =
		startDate !== null &&
		endDate !== null &&
		startDate.isBefore(endDate.add(1, 'day')) &&
		(!isSpecific || selectedClientIds.length > 0) &&
		isFormValid;

	const toggleClient = (clientId: string) => {
		setSelectedClientIds((prev) =>
			prev.includes(clientId) ? prev.filter((id) => id !== clientId) : [...prev, clientId],
		);
	};

	const handleSubmit = () => {
		setSubmitted(true);
		if (!isValid || cancellingParty === null) return;

		dispatch(
			createCancellationRequest({
				providerId: userId,
				startDate: startDate!.format('YYYY-MM-DD'),
				endDate: endDate!.format('YYYY-MM-DD'),
				allClients: !isSpecific,
				clientIds: isSpecific ? selectedClientIds : [],
				cancellingParty,
				reasonId: isAgency ? undefined : Number(reasonId),
				notes: notes.trim() || undefined,
				recurringScheduleId: recurringScheduleId || undefined,
			}),
		)
			.then(unwrapResult)
			.then(() => {
				toast(providerId ? 'Cancellation applied' : 'Cancellation request submitted');
				dispatch(getPendingRequests(undefined));
				onSuccess();
			})
			.catch(errorNormalizer);
	};

	const filteredReasons = cancellingParty === CancellingParty.Provider
		? extendedProviderReasonOptions
		: cancellingParty === CancellingParty.ClientFamily
			? extendedClientReasonOptions
			: [];
	const reasonOptions = filteredReasons.map((r) => ({ id: `${r.id}`, label: r.label }));

	return (
		<div className="cancellationModal">
			<div className="cancellationInfoBanner">
				<InfoFilled color={PRIMARY.BASE} />
				<div>
					<Text title={providerId ? 'Set Cancellation' : 'Request Cancellation'} type="psm" fontWeight="semiBold" />
					<Text
						title={
							providerName
								? `Cancellation will be applied immediately for ${providerName}. Affected sessions will be cancelled.`
								: startDate && endDate && startDate.isSame(endDate, 'day')
									? 'Single-day requests are applied automatically. Affected sessions will be cancelled immediately and your Case Manager will be notified.'
									: 'Your request will be sent to your Case Manager for approval. Sessions will not be cancelled until your Case Manager approves. You will be notified once a decision is made.'
						}
						type="pxs"
						color={TEXT_COLORS.SECONDARY}
					/>
				</div>
			</div>

			<div className="cancellationField">
				<div className="fieldLabelRow">
					<Text title="Cancellation period" type="psm" fontWeight="semiBold" />
					<span className="requiredAsterisk">*</span>
				</div>
				<div className="dateRow">
					<div
						className={`dateColumn ${submitted && (!startDate || (startDate && endDate && startDate.isAfter(endDate))) ? 'fieldError' : ''}`}
					>
						<Picker.CustomDate value={startDate} onChange={setStartDate} />
						<Text title="Start date" type="pxs" color="#6B7280" />
					</div>
					<div
						className={`dateColumn ${submitted && (!endDate || (startDate && endDate && startDate.isAfter(endDate))) ? 'fieldError' : ''}`}
					>
						<Picker.CustomDate value={endDate} onChange={setEndDate} />
						<Text title="End date" type="pxs" color="#6B7280" />
					</div>
				</div>
				{submitted && startDate && endDate && startDate.isAfter(endDate) && (
					<Text title="End date must be on or after start date" type="pxs" color="#ef4444" />
				)}
			</div>

			{clientId && clientName ? (
				<div className="cancellationField">
					<Text title="Which clients are affected?" type="psm" fontWeight="semiBold" />
					<Text title={clientName} type="psm" color="#1F2937" />
				</div>
			) : (
				<div className="cancellationField">
					<div className="fieldLabelRow">
						<Text title="Which clients are affected?" type="psm" fontWeight="semiBold" />
						<span className="requiredAsterisk">*</span>
					</div>
					<CustomSelect
						data={clientScopeOptions}
						value={clientScope}
						setValue={(e) => setClientScope(e.target.value)}
					/>
				</div>
			)}

			{isSpecific && !clientId && (
				<div className="cancellationField">
					<div className="fieldLabelRow">
						<Text title="Select affected clients" type="psm" fontWeight="semiBold" />
						<span className="requiredAsterisk">*</span>
					</div>
					<div className={`clientCheckboxList ${submitted && selectedClientIds.length === 0 ? 'fieldError' : ''}`}>
						{clients.map((client) => (
							<CustomCheckBox
								key={client.id}
								item={{
									id: client.id,
									label: client.fullName,
									checked: selectedClientIds.includes(client.id),
								}}
								onChange={() => toggleClient(client.id)}
							/>
						))}
						{clients.length === 0 && <Text title="No clients found" type="pxs" color={TEXT_COLORS.TERTIARY} />}
					</div>
					{submitted && selectedClientIds.length === 0 && (
						<Text title="Please select at least one client" type="pxs" color="#ef4444" />
					)}
				</div>
			)}

			<CancellationFormFields
				cancellingParty={cancellingParty}
				cancellingPartyOptions={cancellingPartyOptions}
				isAgency={isAgency}
				isOtherReason={isOtherReason}
				notes={notes}
				notesPlaceholder={isOtherReason ? undefined : 'Provide any additional context that might be helpful...'}
				notesRequired={notesRequired}
				onCancellingPartyChange={handleCancellingPartyChange}
				onNotesChange={setNotes}
				onReasonChange={setReasonId}
				partyLabel="Who is requesting this time off?"
				reasonId={reasonId}
				reasonOptions={reasonOptions}
				submitted={submitted}
			/>

			<div className="cancellationActions">
				<CustomButton title="Cancel" buttonType="tertiary" onClick={onClose} />
				<CustomButton
					title={providerId ? 'Set Cancellation' : 'Submit Cancellation Request'}
					buttonType="primary"
					onClick={handleSubmit}
					loading={submitting}
				/>
			</div>
		</div>
	);
};

export default RequestCancellationModal;
